import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loginUserThunk } from "../store/slices/authThunks";
import loginIllustration from "../images/Learning language with friend.png";
import logo from "../images/Nkenne Ai-01 2.png";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role:"ADMIN"
  });
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, role } = useSelector((state) => state.auth);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Only allow admin users to access the dashboard
      if (role === 'admin') {
        // Check if there's a redirect path from location state
        const redirectTo = location.state?.redirectTo;
        
        if (redirectTo) {
          navigate(redirectTo, { 
            state: { 
              selectedPlan: location.state?.selectedPlan 
            } 
          });
        } else {
          navigate('/admin-dashboard');
        }
      } else {
        // If user is authenticated but not admin, show error and stay on login page
        toast.error("Access denied. This is an admin-only dashboard. Please contact your administrator for access.");
      }
    }
  }, [isAuthenticated, role, navigate, location.state]);

  // Clear error when component mounts
  useEffect(() => {
    // dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const result = await dispatch(loginUserThunk(formData));
      if (loginUserThunk.fulfilled.match(result)) {
        // Check if user role is 'user' and prevent login
        if (result.payload.role === 'user') {
          toast.error("Access denied. This is an admin-only dashboard. Please contact your administrator for access.");
          return;
        }
        
        // Only allow admin users to proceed
        if (result.payload.role === 'admin') {
          toast.success("Login successful! Welcome back!");
          navigate('/admin-dashboard');
        } else {
          toast.error("Invalid user role. Only administrators can access this dashboard.");
        }
      } else if (loginUserThunk.rejected.match(result)) {
        toast.error(result.payload || "Login failed. Please check your credentials and try again.");
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error("Login failed. Please try again.");
    }
  };





  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Illustration */}
      <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
        <img
          src={loginIllustration}
          alt="Illustration"
          className="max-w-[70%] h-auto"
        />
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md space-y-6">
          {/* Logo and Branding */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
            </div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-6">
              Sign in to NKENNEAi
            </h1>
          </div>

          <div className="space-y-6 border p-6 rounded-lg">

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter your email"
                  required
                />
              </div>

              {/* Password Input */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  {/* <Link
                    to="/forgot-password"
                    className="text-sm text-secondary hover:text-teal-500"
                  >
                    Forgot password?
                  </Link> */}
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-secondary hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {/* Divider */}
            {/* <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">OR</span>
              </div>
            </div>


            <p className="text-center text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="text-teal-600 hover:text-teal-500 font-medium"
              >
                Sign up
              </Link>
            </p> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
